import React, { useState } from 'react';
import { Add, Remove } from '@mui/icons-material';
import { Button, ButtonGroup, MenuItem, styled, TextField } from '@mui/material';
import { ButtonGroupIconButton } from 'components/ButtonGroup';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '2px 10px',
}));

const StyledButton = styled(Button)<{ isActive?: boolean }>(({ theme, isActive }) => ({
  fontWeight: 'normal',
  padding: '2px 6px',
  transition: 'background-color 0.3s, color 0.3s, transform 0.3s',
  ...(isActive && {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: theme.palette.common.white,
  }),
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(1),
}));

const StyledIconButton = styled(ButtonGroupIconButton)<{
  isLeft?: boolean;
  isRight?: boolean;
  isClicked?: boolean;
}>(({ theme, isLeft, isRight, isClicked }) => ({
  fontWeight: 'normal',
  padding: theme.spacing(0.5),
  ...(isLeft && {
    borderRadius: '5px 0px 0px 5px',
  }),
  ...(isRight && {
    borderRadius: '0px 5px 5px 0px',
  }),
  ...(isClicked && {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.common.white,
    transition: 'background-color 0.3s, transform 0.3s',
  }),
}));

const ONCLICKED_TIMEOUT = 300;

interface AddMultipleMenuItemProps {
  multipleValue: number;
  setMultipleValue: (value: number | ((prevValue: number) => number)) => void;
  onAddMultiple: () => void;
}

const AddMultipleMenuItem: React.FC<AddMultipleMenuItemProps> = ({
  multipleValue,
  setMultipleValue,
  onAddMultiple,
}) => {
  const [decreaseClicked, setDecreaseClicked] = useState(false);
  const [increaseClicked, setIncreaseClicked] = useState(false);

  const onClickChangeStyle = (setState: React.Dispatch<React.SetStateAction<boolean>>) => {
    setState(true);
    setTimeout(() => {
      setState(false);
    }, ONCLICKED_TIMEOUT);
  };

  const handleDecreaseValueOnClick = () => {
    if (multipleValue > 0) {
      setMultipleValue(prevMultipleValue => prevMultipleValue - 1);
      onClickChangeStyle(setDecreaseClicked);
    }
  };

  const handleIncreaseValueOnClick = () => {
    setMultipleValue(prevMultipleValue => prevMultipleValue + 1);
    onClickChangeStyle(setIncreaseClicked);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (!Number.isNaN(newValue)) {
      setMultipleValue(newValue);
    }
  };

  const handleOnAddMultipleClick = () => {
    if (multipleValue > 0) {
      onAddMultiple();
      setMultipleValue(0);
    }
  };

  return (
    <StyledMenuItem disableRipple>
      <StyledButton disableRipple isActive={multipleValue > 0} onClick={handleOnAddMultipleClick}>
        ADD MULTIPLE
      </StyledButton>
      <StyledButtonGroup>
        <StyledIconButton
          onClick={handleDecreaseValueOnClick}
          isLeft
          isClicked={decreaseClicked}
          disableElevation={false}
          fullWidth={false}
          disableFocusRipple={false}>
          <Remove />
        </StyledIconButton>
        <TextField
          variant="standard"
          value={multipleValue}
          onChange={handleInputChange}
          InputProps={{
            disableUnderline: true,
            style: {
              padding: '0px',
              width: '40px',
              borderRadius: '0px',
              backgroundColor: 'white',
            },
            inputProps: {
              style: { textAlign: 'center' },
            },
          }}
        />
        <StyledIconButton
          onClick={handleIncreaseValueOnClick}
          isRight
          isClicked={increaseClicked}
          disableElevation={false}
          fullWidth={false}
          disableFocusRipple={false}>
          <Add />
        </StyledIconButton>
      </StyledButtonGroup>
    </StyledMenuItem>
  );
};

export default AddMultipleMenuItem;
